import { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { useTranslation } from 'react-i18next'
import {
  calculateReturn,
  cleanProcess,
  resetCalculate
} from '@/redux/api/returns-api-slice'
import { useNavigate } from 'react-router'
import {
  CalculateRequest,
  ExchangeSelection,
  Address as ModelsAddress,
  RefundAction
} from '@itsrever/returns-api-types'
import { ReturnMethod, ReturnMethods } from '@/redux/return/return-slice'
import { usePages } from '@/hooks'
import { AxiosError } from 'axios'
import { addToast } from '@heroui/react'
import { getOrderParams } from '@/utils/get-order-params'
import { usePostHog } from '@posthog/react'

export function useCalculate() {
  const { t, i18n } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { pages } = usePages()
  const posthog = usePostHog()

  const calculateCall = useAppSelector((store) => store.returnsApi.calculate)
  const loadingCalculate = calculateCall.loading
  const errorCalculate = calculateCall.error

  const orderInfo = useAppSelector((store) => store.order)
  const settings = useAppSelector(
    (store) => store.settingsApi.settings.response
  )
  const ecommerceID = settings.ecommerce_id
  const order = useAppSelector((store) => store.returnsApi.order.response)

  const refundMethods = useAppSelector(
    (store) =>
      store.refundMethodsApi.refundMethods.response.result?.refund_methods
  )

  const returnStatus = useAppSelector((store) => store.return)
  const userSelectedCarrier = returnStatus.userSelectedCarrier
  const userSelectedProvider = returnStatus.userSelectedProvider

  const returnedItems = returnStatus.returnedItems.map((p) => {
    return p.item
  })
  const newItems = returnStatus.newItems
  const exchangeSelections = newItems.map((item) => {
    const exchangeItem: ExchangeSelection = {
      line_item_platform_id: item.returnedItemId,
      quantity: item.quantity,
      variant_id: item.productVariant.variantId,
      signed_product_raw: item.signedProductRaw,
      collection_ids: item.collectionIds
    }
    return exchangeItem
  })
  const refundPaymentMethod = returnStatus.refundPaymentMethod

  const onlyNotReceived = returnedItems.every(
    (retItem) => retItem.product_return_reason == 'NOT_RECEIVED'
  )

  const onlyExchanges = returnedItems.every(
    (item) => item.action === RefundAction.RefundAction_TO_EXCHANGE
  )

  const pickupRanges = returnStatus.pickUpRanges
  const returnAddress = returnStatus.returnAddress

  const keepYourItemSelected = useAppSelector(
    (store) => store.return.keepYourItem
  )

  const callCalculate = (
    returnMethod: ReturnMethod,
    exchangeShippingAddress?: ModelsAddress
  ) => {
    if (ecommerceID && order.checkout_id) {
      const isInStore = settings.in_store_settings?.is_enabled

      const inStoreRefundMethod =
        exchangeSelections.length > 0
          ? refundMethods?.[0].method
          : refundPaymentMethod.method

      const calculateData: CalculateRequest = {
        checkout_id: order.checkout_id,
        drop_off_address: returnAddress,
        exchange_shipping_address: exchangeShippingAddress,
        exchange_selections: exchangeSelections,
        pickup_address: returnAddress,
        pickup_ranges: pickupRanges,
        return_method: returnMethod,
        items_to_return: returnedItems,
        user_preferred_lang: i18n.language,
        user_selected_carrier: userSelectedCarrier,
        user_selected_provider: userSelectedProvider,
        refund_method:
          isInStore || (onlyNotReceived && onlyExchanges)
            ? inStoreRefundMethod
            : refundPaymentMethod.method,
        keep_your_item_selected: keepYourItemSelected
      }
      dispatch(
        calculateReturn({
          ecommerceID,
          calculateData
        })
      )
      posthog.capture('Return Method', {
        ReturnMethod: ReturnMethods[returnMethod],
        checkout_id: order?.checkout_id
      })
    }
  }

  function handleReject() {
    dispatch(resetCalculate())
    console.warn(errorCalculate)
    const error = errorCalculate as AxiosError

    if (error.response?.status === 406) {
      addToast({
        description: t('toast_errors.error_406')
      })
      return
    }

    if (error.response?.status === 401 || error.response?.status === 419) {
      const params = getOrderParams(
        {
          orderNumber: orderInfo.orderNumber ?? '',
          email: orderInfo.email,
          postalCode: order.order?.shipping_address?.postcode
        },
        settings.identification_flow!
      )

      addToast({
        description: t('toast_errors.error_419')
      })

      navigate({
        pathname: pages.Landing,
        search: params.toString()
      })

      return
    }

    navigate(pages.Error)
  }

  useEffect(() => {
    if (loadingCalculate === 'succeeded') {
      dispatch(resetCalculate())
      dispatch(cleanProcess())
      navigate(pages.Confirmation)
      return
    }

    if (loadingCalculate === 'failed') {
      handleReject()
      return
    }
  }, [loadingCalculate])

  return { callCalculate, loadingCalculate: loadingCalculate === 'pending' }
}
